/**
 * Utility functions for detecting equipment time conflicts
 */

/**
 * Convert time string (HH:MM) to minutes since midnight
 */
export function timeToMinutes(time) {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

/**
 * Convert minutes since midnight to HH:MM string
 */
export function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Check if two time ranges overlap
 */
export function timeRangesOverlap(start1, end1, start2, end2) {
  if (!start1 || !end1 || !start2 || !end2) return false;
  const s1 = timeToMinutes(start1);
  const e1 = timeToMinutes(end1);
  const s2 = timeToMinutes(start2);
  const e2 = timeToMinutes(end2);
  return s1 < e2 && s2 < e1;
}

/**
 * Check if range2 falls entirely within range1
 */
export function timeRangeWithin(outerStart, outerEnd, innerStart, innerEnd) {
  if (!outerStart || !outerEnd || !innerStart || !innerEnd) return false;
  const os = timeToMinutes(outerStart);
  const oe = timeToMinutes(outerEnd);
  const is_ = timeToMinutes(innerStart);
  const ie = timeToMinutes(innerEnd);
  return is_ >= os && ie <= oe;
}

/**
 * Find equipment conflicts within the same section (e.g., two material movements overlapping)
 */
export function findEquipmentConflicts(newEntry, existingEntries, excludeId = null, equipment = []) {
  if (!newEntry.equipment_id || !newEntry.start_time || !newEntry.end_time) {
    return [];
  }
  
  const conflicts = [];
  const getEquipName = (id) => equipment.find(e => e.id === id)?.unit_id || id;
  
  for (const entry of existingEntries) {
    if (excludeId && entry.id === excludeId) continue;
    if (entry.equipment_id !== newEntry.equipment_id) continue;
    
    if (timeRangesOverlap(newEntry.start_time, newEntry.end_time, entry.start_time, entry.end_time)) {
      conflicts.push({
        entry,
        equipmentName: getEquipName(entry.equipment_id),
        existingTime: `${entry.start_time}-${entry.end_time}`,
        newTime: `${newEntry.start_time}-${newEntry.end_time}`,
      });
    }
  }
  
  return conflicts;
}

/**
 * Find cross-section conflicts (equipment used in both Equipment Hours and Material Movements)
 * 
 * When called from 'material' section:
 *   - Does NOT flag if the material movement falls within the equipment's scheduled hours
 *     (this is expected — equipment moves material during its working hours)
 *   - Only flags if the material movement time EXCEEDS the equipment's scheduled window
 * 
 * When called from 'equipment' section:
 *   - Flags if any material movement for this equipment falls outside the new hours window
 */
export function findCrossSectionConflicts(newEntry, equipmentHours, materialMovements, currentSection, excludeId = null, equipment = []) {
  if (!newEntry.equipment_id || !newEntry.start_time || !newEntry.end_time) {
    return [];
  }
  
  const conflicts = [];
  const getEquipName = (id) => equipment.find(e => e.id === id)?.unit_id || id;
  
  if (currentSection === 'material') {
    // Material Movements page: check if this movement falls OUTSIDE equipment hours
    for (const eqEntry of equipmentHours) {
      if (eqEntry.equipment_id !== newEntry.equipment_id) continue;
      
      // If the material movement is within the equipment hours window → no conflict
      if (timeRangeWithin(eqEntry.start_time, eqEntry.end_time, newEntry.start_time, newEntry.end_time)) {
        continue; // This is expected behavior
      }
      
      // If there's an overlap but the movement goes outside → flag it
      if (timeRangesOverlap(newEntry.start_time, newEntry.end_time, eqEntry.start_time, eqEntry.end_time)) {
        // Only flag if the movement exceeds the equipment window
        const ns = timeToMinutes(newEntry.start_time);
        const ne = timeToMinutes(newEntry.end_time);
        const es = timeToMinutes(eqEntry.start_time);
        const ee = timeToMinutes(eqEntry.end_time);
        
        if (ns < es || ne > ee) {
          conflicts.push({
            entry: eqEntry,
            section: 'Equipment Hours',
            equipmentName: getEquipName(eqEntry.equipment_id),
            existingTime: `${eqEntry.start_time}-${eqEntry.end_time}`,
            newTime: `${newEntry.start_time}-${newEntry.end_time}`,
            message: `Movement time exceeds equipment schedule (${eqEntry.start_time}-${eqEntry.end_time})`,
          });
        }
      }
      
      // If no overlap at all → the movement is outside working hours entirely
      if (!timeRangesOverlap(newEntry.start_time, newEntry.end_time, eqEntry.start_time, eqEntry.end_time)) {
        conflicts.push({
          entry: eqEntry,
          section: 'Equipment Hours',
          equipmentName: getEquipName(eqEntry.equipment_id),
          existingTime: `${eqEntry.start_time}-${eqEntry.end_time}`,
          newTime: `${newEntry.start_time}-${newEntry.end_time}`,
          message: `Movement is outside equipment working hours (${eqEntry.start_time}-${eqEntry.end_time})`,
        });
      }
    }
  } else {
    // Equipment Hours page: check if material movements fall outside new equipment window
    for (const mmEntry of materialMovements) {
      if (mmEntry.equipment_id !== newEntry.equipment_id) continue;
      
      // If material movement is within the new equipment hours → no conflict
      if (timeRangeWithin(newEntry.start_time, newEntry.end_time, mmEntry.start_time, mmEntry.end_time)) {
        continue;
      }
      
      if (timeRangesOverlap(newEntry.start_time, newEntry.end_time, mmEntry.start_time, mmEntry.end_time)) {
        conflicts.push({
          entry: mmEntry,
          section: 'Material Movements',
          equipmentName: getEquipName(mmEntry.equipment_id),
          existingTime: `${mmEntry.start_time}-${mmEntry.end_time}`,
          newTime: `${newEntry.start_time}-${newEntry.end_time}`,
        });
      }
    }
  }
  
  return conflicts;
}

/**
 * Calculate effective working time windows by subtracting delays from equipment hours
 * @param {Object} eqHour - Equipment hours entry {start_time, end_time}
 * @param {Array} delays - Delay entries for the same equipment [{start_time, end_time}]
 * @returns {Object} { windows: [{start, end}], totalMinutes, delayMinutes, effectiveMinutes }
 */
export function calculateEffectiveWindows(eqHour, delays = []) {
  if (!eqHour || !eqHour.start_time || !eqHour.end_time) {
    return { windows: [], totalMinutes: 0, delayMinutes: 0, effectiveMinutes: 0 };
  }
  
  const eqStart = timeToMinutes(eqHour.start_time);
  const eqEnd = timeToMinutes(eqHour.end_time);
  const totalMinutes = eqEnd - eqStart;
  
  // Filter delays that have valid times and overlap with the equipment hours
  const relevantDelays = delays
    .filter(d => d.start_time && d.end_time)
    .map(d => ({
      start: Math.max(timeToMinutes(d.start_time), eqStart),
      end: Math.min(timeToMinutes(d.end_time), eqEnd),
    }))
    .filter(d => d.start < d.end)
    .sort((a, b) => a.start - b.start);
  
  // Merge overlapping delays
  const mergedDelays = [];
  for (const d of relevantDelays) {
    if (mergedDelays.length > 0 && d.start <= mergedDelays[mergedDelays.length - 1].end) {
      mergedDelays[mergedDelays.length - 1].end = Math.max(mergedDelays[mergedDelays.length - 1].end, d.end);
    } else {
      mergedDelays.push({ ...d });
    }
  }
  
  // Calculate effective windows (equipment hours minus delays)
  const windows = [];
  let cursor = eqStart;
  for (const d of mergedDelays) {
    if (cursor < d.start) {
      windows.push({ start: minutesToTime(cursor), end: minutesToTime(d.start) });
    }
    cursor = Math.max(cursor, d.end);
  }
  if (cursor < eqEnd) {
    windows.push({ start: minutesToTime(cursor), end: minutesToTime(eqEnd) });
  }
  
  const delayMinutes = mergedDelays.reduce((sum, d) => sum + (d.end - d.start), 0);
  const effectiveMinutes = totalMinutes - delayMinutes;
  
  return { windows, totalMinutes, delayMinutes, effectiveMinutes };
}
